import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Permission } from '../../../lib/api/condo';
import { hasPermissions, type WebRole } from '../lib/permissions';
import { useCondoStore } from '../store/condoStore';
import { LoadingState } from './States';

interface CondoRoleGuardProps {
  /** Rol activo permitido. Vacio = cualquier rol web. */
  roles?: WebRole[];
  permission?: Permission | Permission[];
  mode?: 'all' | 'any';
  fallback?: ReactNode;
  children: ReactNode;
}

export function CondoRoleGuard({ roles, permission, mode = 'all', fallback, children }: CondoRoleGuardProps) {
  const { t } = useTranslation();
  const { activeRole, uiPermissions: permissions, permStatus, condos, activeCondoId } = useCondoStore();

  if (permStatus === 'idle' || permStatus === 'loading') return <LoadingState />;

  const mine = condos.find((c) => c.id === activeCondoId);
  const roleOk = !!activeRole && !!mine?.roles?.includes(activeRole) && (!roles?.length || roles.includes(activeRole));
  const permOk = permStatus === 'ready' && (!permission || hasPermissions(permissions, permission, mode));

  if (roleOk && permOk) return <>{children}</>;
  if (fallback !== undefined) return <>{fallback}</>;
  return (
    <div role="alert" data-testid="hoa-guard-denied" className="m-6 rounded-xl border border-rose-200 bg-rose-50 p-4 text-rose-800 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-200">
      <p className="font-semibold">{t('condo.guard.denied_title')}</p>
      <p className="text-sm">{t('condo.guard.denied_body')}</p>
    </div>
  );
}
